import './sentry'
import * as Sentry from '@sentry/react'
import ReactDOM from 'react-dom/client'

import App from './App'
import trunk from './configureStore'
import { getUser } from './services/user.service'
import { store } from './stores/RootStore'
import './index.css'

const root = document.getElementById('root')

/**
 * Initialize the app by loading persisted state and refreshing user data if authenticated
 */
const initializeApp = async () => {
  // Load persisted state from localStorage
  await trunk.init()

  // If user is authenticated, fetch fresh user data to ensure roles and entities are up-to-date
  if (store.user.isAuthenticated && store.user.userId) {
    try {
      const freshUserData = await getUser(store.user.userId)
      store.user.setUser(freshUserData)
    } catch (error) {
      // Only log out when the server explicitly rejected the session. A
      // network error (dev server restarting, brief outage) or a 5xx must not
      // wipe a valid persisted session — the axios interceptor already
      // refreshes/logs out on real token failures. Mirrors the guard in
      // RootStore.triggerRefreshToken.
      const status = error.response?.status ?? error.cause?.status
      const isAuthFailure = status === 401 || status === 403
      // eslint-disable-next-line no-console
      console.warn('Failed to refresh user data on app load:', error)
      if (isAuthFailure) {
        store.clearStore()
      }
    }
  }
}

initializeApp().then(() =>
  ReactDOM.createRoot(root, {
    // Callback called when an error is thrown and not caught by an ErrorBoundary.
    onUncaughtError: Sentry.reactErrorHandler((error, errorInfo) => {
      // eslint-disable-next-line no-console
      console.warn('Uncaught error', error, errorInfo.componentStack)
    }),
    // Callback called when React catches an error in an ErrorBoundary.
    onCaughtError: Sentry.reactErrorHandler(),
    // Callback called when React automatically recovers from errors.
    onRecoverableError: Sentry.reactErrorHandler(),
  }).render(<App />),
)
